// One place that knows how many decimals a figure on the ENQ -> invoice chain
// may carry, on the screen side. Every typing box, `step=`, keystroke guard and
// figure display imports these three numbers instead of repeating a bare 2 / 4
// / 6 of its own (card: six-decimals-across-the-chain, AC11).
//
// backend/utils/precision.py holds the SAME three numbers for the stored
// columns and the server's narrowing gate. The two files must change together:
// a screen that lets a 7th decimal through is refused by the server, and a
// screen that stops at 5 clips a figure the server would have kept.
//
// BigInt is an ES2020 built-in the react-app lint environment (es6) does not
// list; without this line the dev server refuses to compile the file.
/* global BigInt */
import { canonicalizeAmount } from './amountFormat';

// Per-unit prices (enquiry, recap, delivery, invoice / credit / debit lines).
export const PRICE_DECIMALS = 6;
// Quantities (tonnage, litres, units).
export const QUANTITY_DECIMALS = 6;
// Money that is actually paid: line net, VAT, gross, totals, payments.
export const AMOUNT_DECIMALS = 2;

// Native <input step=...> for a decimal count: 6 -> '0.000001', 2 -> '0.01',
// 0 -> '1'.
export const stepFor = (decimals) => {
  const places = Math.max(0, Math.floor(Number(decimals) || 0));
  return places === 0 ? '1' : `0.${'0'.repeat(places - 1)}1`;
};

// A JS number as a plain dot-decimal string, never exponent notation
// (1e-7 -> '0.0000001', 1e21 -> '1000000000000000000000').
const numberToPlain = (n) => {
  if (!Number.isFinite(n)) return '';
  const s = String(n);
  const match = s.match(/^(-?)(\d+)(?:\.(\d+))?e([+-]\d+)$/i);
  if (!match) return s;
  const [, sign, intPart, fracPart = '', expText] = match;
  const exp = Number(expText);
  const digits = intPart + fracPart;
  const point = intPart.length + exp; // position of the decimal point in `digits`
  if (point <= 0) return `${sign}0.${'0'.repeat(-point)}${digits}`;
  if (point >= digits.length) return `${sign}${digits}${'0'.repeat(point - digits.length)}`;
  return `${sign}${digits.slice(0, point)}.${digits.slice(point)}`;
};

// Split a figure into sign / whole-number digits / decimal digits WITHOUT going
// through a float for string input. null when the value is not a number.
const parseFigure = (value) => {
  if (value === null || value === undefined) return null;
  const raw = typeof value === 'number' ? numberToPlain(value) : value;
  const canon = canonicalizeAmount(raw);
  if (canon === '') return null;
  const match = canon.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) return null;
  return { negative: match[1] === '-', intDigits: match[2], fracDigits: match[3] || '' };
};

// Round a non-negative scaled integer `magnitude` (carrying `scale` decimals)
// to `places` decimals, half-up with ties away from zero -- the same rule as
// the server's Python ROUND_HALF_UP, because the sign is applied afterwards.
const roundScaled = (magnitude, scale, places) => {
  if (scale <= places) return magnitude * 10n ** BigInt(places - scale);
  const divisor = 10n ** BigInt(scale - places);
  const quotient = magnitude / divisor;
  const remainder = magnitude % divisor;
  return remainder * 2n >= divisor ? quotient + 1n : quotient;
};

// A non-negative scaled integer back to 'int.frac' with exactly `places`
// decimals, signed only when it is not zero.
const scaledToString = (magnitude, places, negative) => {
  const digits = magnitude.toString().padStart(places + 1, '0');
  const intPart = digits.slice(0, digits.length - places);
  const fracPart = places > 0 ? `.${digits.slice(digits.length - places)}` : '';
  const sign = negative && magnitude !== 0n ? '-' : '';
  return `${sign}${intPart}${fracPart}`;
};

// A figure for display: plain dot-decimal, no grouping, at least `minPlaces`
// and at most `maxPlaces` decimals. Decimals beyond `minPlaces` show only when
// the figure genuinely carries them, so the server's fixed-scale strings lose
// their phantom zeros ('512.340000' -> '512.34'). Rounds (half-up, ties away
// from zero) only when more than `maxPlaces` digits are present.
//   formatFigure('512.340000', 2) -> '512.34'
//   formatFigure('500', 2)        -> '500.00'
//   formatFigure('320.123412', 4) -> '320.123412'
//   formatFigure(512.3, 2)        -> '512.30'
export const formatFigure = (value, minPlaces, maxPlaces = PRICE_DECIMALS) => {
  const parsed = parseFigure(value);
  if (!parsed) return '';
  const min = Math.max(0, Math.floor(Number(minPlaces) || 0));
  const max = Math.max(min, Math.floor(Number(maxPlaces) || 0));
  const { negative, intDigits, fracDigits } = parsed;
  const scale = fracDigits.length;
  const magnitude = BigInt(intDigits + fracDigits);
  const places = Math.min(scale, max);
  const rounded = roundScaled(magnitude, scale, places);
  const fixed = scaledToString(rounded, places, negative);
  const [intPart, fracPart = ''] = fixed.split('.');
  const kept = fracPart.replace(/0+$/, '').padEnd(min, '0');
  return kept ? `${intPart}.${kept}` : intPart;
};

// The EXACT decimal product a * b, rounded to exactly `places` decimals
// (half-up, ties away from zero). Scaled BigInts, never a float: 1.005 * 1 as
// a float is 1.00499999..., which rounds to 1.00 where the server says 1.01.
// Empty or unreadable input counts as zero.
export const multiplyRound = (a, b, places) => {
  const left = parseFigure(a);
  const right = parseFigure(b);
  const target = Math.max(0, Math.floor(Number(places) || 0));
  if (!left || !right) return scaledToString(0n, target, false);
  const product = BigInt(left.intDigits + left.fracDigits) * BigInt(right.intDigits + right.fracDigits);
  const scale = left.fracDigits.length + right.fracDigits.length;
  const rounded = roundScaled(product, scale, target);
  return scaledToString(rounded, target, left.negative !== right.negative);
};

// The EXACT decimal sum a + b, with at most `maxPlaces` decimals and no
// phantom zeros: the difference a - (-b). As floats 0.1 + 0.2 is
// 0.30000000000000004. Empty or unreadable input counts as zero.
//   addFigures('0.1', '0.2', 6)         -> '0.3'
//   addFigures('1950.38', '1950.38', 2) -> '3900.76'
export const addFigures = (a, b, maxPlaces = PRICE_DECIMALS) => {
  const right = parseFigure(b);
  if (!right) return subtractFigures(a, '0', maxPlaces);
  const negated = `${right.negative ? '' : '-'}${right.intDigits}${right.fracDigits ? `.${right.fracDigits}` : ''}`;
  return subtractFigures(a, negated, maxPlaces);
};

// The EXACT decimal difference a - b, with at most `maxPlaces` decimals
// (half-up, ties away from zero, only past that) and no phantom zeros.
// Scaled BigInts, never a float: 430000.123412 - 100000.1 as floats is
// 330000.02341200004. Empty or unreadable input counts as zero.
//   subtractFigures('430000.123412', '100000.1', 6) -> '330000.023412'
//   subtractFigures('100', '100.5', 6)              -> '-0.5'
export const subtractFigures = (a, b, maxPlaces = PRICE_DECIMALS) => {
  const zero = { negative: false, intDigits: '0', fracDigits: '' };
  const left = parseFigure(a) || zero;
  const right = parseFigure(b) || zero;
  const scale = Math.max(left.fracDigits.length, right.fracDigits.length);
  const signed = ({ negative, intDigits, fracDigits }) => {
    const magnitude = BigInt(intDigits + fracDigits.padEnd(scale, '0'));
    return negative ? -magnitude : magnitude;
  };
  const difference = signed(left) - signed(right);
  const negative = difference < 0n;
  const exact = scaledToString(negative ? -difference : difference, scale, negative);
  return formatFigure(exact, 0, maxPlaces);
};

// A line's net money: quantity x unit price, to the cent, exactly as the
// server computes it.
export const lineNet = (quantity, unitPrice) => multiplyRound(quantity, unitPrice, AMOUNT_DECIMALS);
